import React, { createContext, useEffect, useState, useMemo } from 'react';
import { ThemeColors, lightTheme, darkTheme, themeToCssVars, themePresets, createTheme } from '@/lib/themes';

// Base themes available in the system
export type BaseTheme = 'dark' | 'light' | 'system';

// Available theme presets (including custom ones)
export type ThemePreset = 
  | 'default'
  | 'solarized' 
  | 'nord'
  | 'berry'
  | 'custom';

export type ThemeContextType = {
    // Base theme (dark/light/system)
    theme: BaseTheme;
    setTheme: (theme: BaseTheme) => void;
    toggleTheme: () => void;
    currentTheme: 'dark' | 'light'; // The actual applied theme (dark or light)
    
    // Theme preset selection
    themePreset: ThemePreset;
    setThemePreset: (preset: ThemePreset) => void;
    
    // Custom theming capabilities
    customLightTheme: ThemeColors;
    customDarkTheme: ThemeColors;
    setCustomLightTheme: (theme: ThemeColors) => void;
    setCustomDarkTheme: (theme: ThemeColors) => void;
    applyThemeColorOverrides: (overrides: Partial<ThemeColors>, mode?: 'dark' | 'light' | 'both') => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
    children: React.ReactNode;
    initialLightTheme?: ThemeColors;
    initialDarkTheme?: ThemeColors;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    initialLightTheme,
    initialDarkTheme,
}) => {
    // Initialize base theme preference (dark/light/system)
    const [theme, setTheme] = useState<BaseTheme>(() => {
        // Check for saved theme preference
        const savedTheme = localStorage.getItem('theme') as BaseTheme;
        if (savedTheme && ['dark', 'light', 'system'].includes(savedTheme)) {
            return savedTheme;
        }

        return 'system'; // Default to system
    });

    // Initialize theme preset preference
    const [themePreset, setThemePreset] = useState<ThemePreset>(() => {
        const savedPreset = localStorage.getItem('themePreset') as ThemePreset;
        if (savedPreset && ['default', 'solarized', 'nord', 'berry', 'custom'].includes(savedPreset)) {
            return savedPreset;
        }
        return 'berry'; // Set berry as the default theme
    });

    // Initialize custom themes (from localStorage or props or defaults)
    const [customLightTheme, setCustomLightTheme] = useState<ThemeColors>(() => {
        try {
            const savedCustomLight = localStorage.getItem('customLightTheme');
            if (savedCustomLight) {
                return JSON.parse(savedCustomLight);
            }
        } catch (e) {
            console.error('Failed to parse custom light theme', e);
        }
        return initialLightTheme || lightTheme;
    });

    const [customDarkTheme, setCustomDarkTheme] = useState<ThemeColors>(() => {
        try {
            const savedCustomDark = localStorage.getItem('customDarkTheme');
            if (savedCustomDark) {
                return JSON.parse(savedCustomDark);
            }
        } catch (e) {
            console.error('Failed to parse custom dark theme', e);
        }
        return initialDarkTheme || darkTheme;
    });

    // Determine which actual theme colors to use based on preset
    const activeThemes = useMemo(() => {
        let activeLight = lightTheme;
        let activeDark = darkTheme;

        switch (themePreset) {
            case 'solarized':
                activeLight = themePresets.solarizedLight;
                activeDark = themePresets.solarizedDark;
                break;
            case 'nord':
                activeLight = themePresets.nordLight;
                activeDark = themePresets.nordDark;
                break;
            case 'berry':
                activeLight = themePresets.berryLight;
                activeDark = themePresets.berryDark;
                break;
            case 'custom':
                activeLight = customLightTheme;
                activeDark = customDarkTheme;
                break;
            default:
                // Use default themes
                break;
        }

        return { light: activeLight, dark: activeDark };
    }, [themePreset, customLightTheme, customDarkTheme]);

    // Determine the actual theme to apply based on system preference if needed
    const [currentTheme, setCurrentTheme] = useState<'dark' | 'light'>(
        theme === 'system'
            ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
            : (theme as 'dark' | 'light')
    );

    // Toggle theme function (cycles through dark, light, system)
    const toggleTheme = () => {
        setTheme(prevTheme => {
            if (prevTheme === 'dark') return 'light';
            if (prevTheme === 'light') return 'system';
            return 'dark';
        });
    };

    // Function to update custom theme colors
    const applyThemeColorOverrides = (overrides: Partial<ThemeColors>, mode: 'dark' | 'light' | 'both' = 'both') => {
        if (mode === 'light' || mode === 'both') {
            setCustomLightTheme(prev => createTheme(prev, overrides));
        }
        
        if (mode === 'dark' || mode === 'both') {
            setCustomDarkTheme(prev => createTheme(prev, overrides));
        }
        
        // Set preset to custom after applying overrides
        setThemePreset('custom');
    };

    // Listen for system theme changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleChange = () => {
            if (theme === 'system') {
                setCurrentTheme(mediaQuery.matches ? 'dark' : 'light');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);

    // Update current theme when theme setting changes
    useEffect(() => {
        if (theme === 'system') {
            const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            setCurrentTheme(isDarkMode ? 'dark' : 'light');
        } else {
            setCurrentTheme(theme as 'dark' | 'light');
        }
    }, [theme]);

    // Persist theme settings to localStorage
    useEffect(() => {
        localStorage.setItem('theme', theme);
        localStorage.setItem('themePreset', themePreset);
        
        if (themePreset === 'custom') {
            localStorage.setItem('customLightTheme', JSON.stringify(customLightTheme));
            localStorage.setItem('customDarkTheme', JSON.stringify(customDarkTheme));
        }
    }, [theme, themePreset, customLightTheme, customDarkTheme]);

    // Apply the current theme
    useEffect(() => {
        // Update document class for styling
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(currentTheme);

        // Get the active theme colors based on current theme
        const activeColors = currentTheme === 'dark' ? activeThemes.dark : activeThemes.light;
        
        // Apply CSS variables
        const cssVars = themeToCssVars(activeColors);
        for (const [key, value] of Object.entries(cssVars)) {
            root.style.setProperty(key, value);
        }
    }, [currentTheme, activeThemes]);

    return (
        <ThemeContext.Provider 
            value={{ 
                theme, 
                setTheme, 
                toggleTheme, 
                currentTheme,
                themePreset,
                setThemePreset,
                customLightTheme,
                customDarkTheme,
                setCustomLightTheme,
                setCustomDarkTheme,
                applyThemeColorOverrides
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeContext;